import React, { useState, useEffect, useCallback } from 'react';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { useParams, useNavigate } from 'react-router-dom';
import SALayout from './SALayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  ArrowLeft, Building2, Users, Save, Loader2, Shield, Ban, PlayCircle, LogIn, Flag, Infinity as InfinityIcon,
} from 'lucide-react';
import SAFeatureFlagsTab from './SAFeatureFlagsTab';
import { SpendCard, DataCleanupCard } from './SAOrgSpendCleanup';

export default function SAOrgDetail() {
  const { apiFetch } = useSuperAdmin();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingInternal, setTogglingInternal] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [subEdit, setSubEdit] = useState({
    plan: 'free', seat_limit: 5, price_per_user: 0, usd_price_per_user: '', sd_agent_price_usd: '', trial_ends_at: '', is_active: true,
  });

  const fetchOrg = useCallback(async () => {
    try {
      const res = await apiFetch(`/organizations/${orgId}`);
      if (!res.ok) {
        toast.error('Organization not found');
        navigate('/superadmin/organizations');
        return;
      }
      const data = await res.json();
      setOrg(data);
      const sub = data.subscription || {};
      // Use the backend-computed effective_plan when present (collapses
      // the (plan, plan_tier, trial_expired, period_end) tuple into one of:
      //   free | trial | trial_expired | business | premium | plan_expired
      // Fall back to a local computation for older payloads that pre-date
      // the field.
      const planForUI = data.effective_plan || (() => {
        if (sub.plan === 'active' && sub.current_period_end && new Date(sub.current_period_end) < new Date()) return 'plan_expired';
        if (sub.plan_tier === 'premium') return 'premium';
        if (sub.plan === 'trial') return (sub.trial_ends_at && new Date(sub.trial_ends_at) < new Date()) ? 'trial_expired' : 'trial';
        if (sub.plan === 'active') return 'business';
        return sub.plan || 'free';
      })();
      setSubEdit({
        plan: planForUI,
        seat_limit: data.workspace_settings?.seat_limit || sub.billable_users || sub.seats || 5,
        price_per_user: sub.price_per_user ?? data.effective_price ?? 0,
        usd_price_per_user: sub.usd_price_per_user ?? '',
        sd_agent_price_usd: sub.sd_agent_price_usd ?? '',
        trial_ends_at: sub.trial_ends_at ? new Date(sub.trial_ends_at).toISOString().split('T')[0] : '',
        current_period_end: sub.current_period_end ? new Date(sub.current_period_end).toISOString().split('T')[0] : '',
        is_active: sub.status !== 'inactive',
      });
    } finally { setLoading(false); }
  }, [apiFetch, orgId, navigate]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  const handleSaveSub = async () => {
    setSaving(true);
    try {
      const isTrialish = subEdit.plan === 'trial' || subEdit.plan === 'trial_expired';
      const body = { ...subEdit };
      // Route the single date field to the correct backend key: trial plans
      // use trial_ends_at; paid plans (business/premium/plan_expired) use
      // current_period_end so the SA date stays in sync with the portal.
      if (isTrialish) {
        body.trial_ends_at = body.trial_ends_at || null;
        body.current_period_end = undefined;
      } else {
        body.current_period_end = body.current_period_end || null;
        body.trial_ends_at = null;
      }
      const res = await apiFetch(`/organizations/${orgId}/subscription`, { method: 'PUT', body: JSON.stringify(body) });
      if (res.ok) {
        toast.success('Subscription updated');
        fetchOrg();
      } else {
        toast.error('Failed to save');
      }
    } finally { setSaving(false); }
  };

  const toggleInternal = async (next) => {
    const labels = next
      ? { title: `Mark "${org.name}" as Internal?`, desc: 'Billing / expiry will be bypassed — features ruled by plan_tier forever. Use only for internal Inspiro workspaces.', confirm: 'Mark Internal' }
      : { title: `Remove Internal flag from "${org.name}"?`, desc: 'Standard billing + renewal enforcement resumes immediately. If the paid window has lapsed, features will revert to Free.', confirm: 'Remove Flag' };
    const ok = await confirm({ title: labels.title, description: labels.desc, confirmText: labels.confirm });
    if (!ok) return;
    setTogglingInternal(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/internal`, { method: 'PUT', body: JSON.stringify({ is_internal: next }) });
      if (res.ok) { toast.success(next ? 'Marked as Internal (Lifetime Free)' : 'Internal flag removed'); fetchOrg(); }
      else toast.error('Failed to update flag');
    } finally { setTogglingInternal(false); }
  };

  const doStatus = async (action) => {
    const labels = { suspend: 'Suspend organization', activate: 'Activate organization', delete: 'Delete organization', restore: 'Restore organization' };
    const destructive = action === 'suspend' || action === 'delete';
    const ok = await confirm({
      title: labels[action],
      description: destructive
        ? `Are you sure you want to ${action} "${org.name}"? Members will be affected immediately.`
        : `This will ${action} "${org.name}".`,
      confirmText: labels[action].split(' ')[0],
      variant: destructive ? 'destructive' : 'default',
    });
    if (!ok) return;
    const res = await apiFetch(`/organizations/${orgId}/status`, { method: 'PUT', body: JSON.stringify({ action, reason: `SA action: ${action}` }) });
    if (res.ok) { toast.success(`Org ${action}d`); fetchOrg(); }
    else toast.error(`Failed to ${action}`);
  };

  // "Login as Admin" — mints an access_token for the org owner, sets the
  // regular cookies, then hard-reloads into /dashboard so AuthContext picks
  // up the new session. The stop button in the banner clears everything.
  const loginAsAdmin = async () => {
    const ok = await confirm({
      title: `Login as Admin of "${org.name}"?`,
      description: 'All actions performed will be tagged in the audit log as impersonated by you. Use only for customer support triage.',
      confirmText: 'Login as Admin',
    });
    if (!ok) return;
    setImpersonating(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/impersonate`, { method: 'POST', body: JSON.stringify({}) });
      const data = await res.json();
      if (!res.ok) { toast.error(data?.detail || 'Impersonation failed'); return; }
      toast.success(`Logged in as ${data.target_user?.name || data.target_user?.email}`);
      window.location.href = '/dashboard';
    } finally { setImpersonating(false); }
  };

  if (loading) {
    return <SALayout><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></SALayout>;
  }
  if (!org) return <SALayout><div className="text-slate-400">Not found</div></SALayout>;

  return (
    <SALayout>
      <div className="space-y-5" data-testid="sa-org-detail">
        <button onClick={() => navigate('/superadmin/organizations')} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900" data-testid="sa-org-back">
          <ArrowLeft className="w-3.5 h-3.5" /> All Organizations
        </button>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{org.name}</h1>
              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                <span>{org.slug}</span>
                <span>·</span>
                <span>{org.member_count} members</span>
                <span>·</span>
                <span>Created {new Date(org.created_at).toLocaleDateString()}</span>
                <Badge variant={org.is_active ? 'default' : 'destructive'} className="ml-1" data-testid="sa-org-status-badge">
                  {org.is_suspended ? 'Suspended' : org.is_deleted ? 'Deleted' : 'Active'}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={loginAsAdmin}
              disabled={impersonating || !org.is_active}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="sa-login-as-admin"
            >
              {impersonating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <LogIn className="w-3.5 h-3.5 mr-1" />}
              Login as Admin
            </Button>
            {org.is_suspended
              ? <Button size="sm" variant="outline" onClick={() => doStatus('activate')} data-testid="sa-org-activate"><PlayCircle className="w-3.5 h-3.5 mr-1" /> Activate</Button>
              : <Button size="sm" variant="outline" onClick={() => doStatus('suspend')} className="text-red-600" data-testid="sa-org-suspend"><Ban className="w-3.5 h-3.5 mr-1" /> Suspend</Button>
            }
          </div>
        </div>

        {/* Lifetime spend + recent transactions overview */}
        <SpendCard apiFetch={apiFetch} orgId={orgId} />

        {/* Subscription */}
        <Card className="border-slate-200/60">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4" /> Subscription</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Internal org ("Lifetime Free") toggle — SA-only switch that
                exempts the org from billing / expiry / renewal reminders.
                Plan caps still apply, so to grant Premium features set
                plan_tier='premium' in the dropdown below. */}
            <div
              className={`sm:col-span-2 rounded-xl border px-4 py-3 flex items-start justify-between gap-3 ${
                org.is_internal
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-slate-50 border-slate-200 text-slate-700'
              }`}
              data-testid="sa-internal-toggle-row"
            >
              <div className="flex items-start gap-3">
                <InfinityIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[12px] uppercase tracking-[0.14em] font-bold opacity-80">
                    {org.is_internal ? 'Internal workspace · Lifetime free' : 'Internal workspace flag'}
                  </p>
                  <p className="text-[11px] opacity-80 mt-0.5">
                    {org.is_internal
                      ? 'Billing, renewals, and expiry are bypassed for this organization. Plan tier above still governs feature access.'
                      : 'Toggle on to grant lifetime free access (skip billing / expiry). Use only for internal Inspiro workspaces.'}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant={org.is_internal ? 'outline' : 'default'}
                onClick={() => toggleInternal(!org.is_internal)}
                disabled={togglingInternal}
                className={org.is_internal ? 'border-emerald-300 text-emerald-900 hover:bg-emerald-100' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                data-testid="sa-internal-toggle-btn"
              >
                {togglingInternal
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : org.is_internal ? 'Remove flag' : 'Mark internal'}
              </Button>
            </div>
            {/* Renewal / expiry banner — top of the subscription card so SA
                can see billing state at a glance without opening any row. */}
            {(() => {
              if (org.is_internal) return null;
              const sub = org.subscription || {};
              const end = sub.current_period_end || sub.trial_ends_at;
              if (!end) return null;
              const endDate = new Date(end);
              const now = new Date();
              const daysLeft = Math.ceil((endDate - now) / 86400000);
              const expired = daysLeft < 0;
              const expiringSoon = daysLeft >= 0 && daysLeft <= 7;
              const isCancelled = !!sub.cancel_at_period_end && sub.plan === 'active';
              // Label + tone: cancelled subs get the orange "CANCELS ON"
              // treatment so SA can spot churn candidates at a glance,
              // independent of how many days are left.
              const label = isCancelled
                ? 'Cancels on'
                : sub.plan === 'trial'
                  ? (expired ? 'Trial expired' : 'Trial ends')
                  : sub.plan === 'active'
                    ? (expired ? 'Subscription expired' : 'Renews on')
                    : sub.plan === 'expired'
                      ? 'Subscription expired'
                      : 'Valid until';
              const tone = isCancelled
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : expired
                  ? 'bg-red-50 border-red-200 text-red-900'
                  : expiringSoon
                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                    : 'bg-emerald-50 border-emerald-200 text-emerald-900';
              return (
                <div className={`sm:col-span-2 rounded-xl border px-4 py-3 flex items-start justify-between gap-3 ${tone}`} data-testid="sa-sub-renewal-banner">
                  <div className="flex items-start gap-3">
                    <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[12px] uppercase tracking-[0.14em] font-bold opacity-70">{label}</p>
                      <p className="text-[18px] font-bold leading-tight mt-0.5">
                        {endDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                      <p className="text-[11px] opacity-80 mt-0.5">
                        {expired
                          ? `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago · features revert to Free until renewed`
                          : isCancelled
                            ? `Cancelled by user · ${daysLeft} day${daysLeft !== 1 ? 's' : ''} of access remaining · auto-downgrades to Free at period end`
                            : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining${sub.billing_cycle ? ' · ' + sub.billing_cycle + ' cycle' : ''}`}
                      </p>
                    </div>
                  </div>
                  {isCancelled && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md bg-white/80 border border-current" data-testid="sa-sub-cancelled-chip">
                      Cancelled
                    </span>
                  )}
                </div>
              );
            })()}
            <div>
              <Label className="text-xs text-slate-500">Plan</Label>
              <select
                value={subEdit.plan}
                onChange={e => {
                  const next = e.target.value;
                  setSubEdit(v => {
                    const patch = { ...v, plan: next };
                    // Sensible defaults per option to save SA from filling
                    // every field manually. Trial → 14-day window. Business
                    // / Premium → suggest list price if SA hasn't set one.
                    if (next === 'trial' && !v.trial_ends_at) {
                      const d = new Date(Date.now() + 14 * 86400000);
                      patch.trial_ends_at = d.toISOString().split('T')[0];
                    }
                    if (next === 'business' && (!v.price_per_user || v.price_per_user === 0)) patch.price_per_user = 399;
                    if (next === 'premium') {
                      if (!v.price_per_user || v.price_per_user === 0 || v.price_per_user === 399) patch.price_per_user = 699;
                      // Premium contract requires minimum 20 seats.
                      if (!v.seat_limit || v.seat_limit < 20) patch.seat_limit = 20;
                    }
                    if (next === 'free' || next === 'trial_expired') {
                      patch.price_per_user = 0;
                    }
                    return patch;
                  });
                }}
                className="mt-1 h-9 px-3 text-sm rounded-lg border border-slate-200 bg-white w-full"
                data-testid="sa-sub-plan"
              >
                <option value="free">Free</option>
                <option value="trial">Trial (14-day Business)</option>
                <option value="trial_expired">Trial Expired</option>
                <option value="business">Business — ₹399/user · 10 GB</option>
                <option value="premium">Premium — ₹699/user · 200 GB · min 20 seats</option>
                <option value="plan_expired">Plan Expired (paid period ended)</option>
              </select>
              {/* Effective-state badge: surfaces when the dropdown selection
                  differs from the org's persisted plan, OR when a Trial has
                  expired and we want to call that out clearly. */}
              {org.effective_plan && (
                <p className="text-[10px] text-slate-400 mt-1" data-testid="sa-sub-effective">
                  Currently effective: <span className="font-semibold text-slate-600 uppercase tracking-wider">{org.effective_plan.replace('_', ' ')}</span>
                  {org.effective_plan === 'trial_expired' && org.trial_ends_at && (
                    <> · Trial ended {new Date(org.trial_ends_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</>
                  )}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Seat Limit</Label>
              <Input type="number" value={subEdit.seat_limit}
                onChange={e => setSubEdit(v => ({ ...v, seat_limit: Number(e.target.value) }))}
                className="mt-1 h-9 text-sm" data-testid="sa-sub-seats" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Price / User <span className="text-slate-400 font-normal">(INR)</span></Label>
              <Input type="number" value={subEdit.price_per_user}
                onChange={e => setSubEdit(v => ({ ...v, price_per_user: Number(e.target.value) }))}
                className="mt-1 h-9 text-sm" data-testid="sa-sub-price" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">USD Price / User <span className="text-slate-400 font-normal">(override, blank = list)</span></Label>
              <Input type="number" value={subEdit.usd_price_per_user}
                onChange={e => setSubEdit(v => ({ ...v, usd_price_per_user: e.target.value }))}
                placeholder="e.g. 6" className="mt-1 h-9 text-sm" data-testid="sa-sub-usd-price" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">USD Service Desk / Agent <span className="text-slate-400 font-normal">(override, blank = list)</span></Label>
              <Input type="number" value={subEdit.sd_agent_price_usd}
                onChange={e => setSubEdit(v => ({ ...v, sd_agent_price_usd: e.target.value }))}
                placeholder="e.g. 12" className="mt-1 h-9 text-sm" data-testid="sa-sub-usd-sd-price" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Period End Date <span className="text-slate-400 font-normal">(override)</span></Label>
              <Input type="date"
                value={(subEdit.plan === 'trial' || subEdit.plan === 'trial_expired') ? subEdit.trial_ends_at : subEdit.current_period_end}
                onChange={e => {
                  const val = e.target.value;
                  const isTrialish = subEdit.plan === 'trial' || subEdit.plan === 'trial_expired';
                  setSubEdit(v => isTrialish ? ({ ...v, trial_ends_at: val }) : ({ ...v, current_period_end: val }));
                }}
                disabled={subEdit.plan === 'free'}
                className="mt-1 h-9 text-sm" data-testid="sa-sub-trial" />
              <p className="text-[10px] text-slate-400 mt-1" data-testid="sa-sub-period-helper">
                {subEdit.plan === 'trial' && 'Trial end date. Set to a future date to extend the Business trial window.'}
                {subEdit.plan === 'trial_expired' && 'Historical trial-end date (used for portal "Trial ended on…" message).'}
                {subEdit.plan === 'plan_expired' && 'Date the paid period lapsed.'}
                {subEdit.plan === 'business' && 'Next renewal / paid-period end. Extend for goodwill or shorten to force early re-billing.'}
                {subEdit.plan === 'premium' && 'Next renewal / paid-period end. Extend for goodwill or shorten to force early re-billing.'}
                {subEdit.plan === 'free' && 'Not applicable on Free plan.'}
                {org.subscription?.cancel_at_period_end && (
                  <span className="block mt-1 text-amber-700 font-medium">Cancellation scheduled — user loses access on this date unless they resume.</span>
                )}
              </p>
            </div>
            <div className="sm:col-span-2 pt-2">
              <Button onClick={handleSaveSub} disabled={saving} className="h-9 rounded-xl" data-testid="sa-sub-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Save Subscription</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Feature flags & overrides */}
        <Card className="border-slate-200/60">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Flag className="w-4 h-4" /> Feature flags & overrides</CardTitle>
          </CardHeader>
          <CardContent>
            <SAFeatureFlagsTab orgId={orgId} />
          </CardContent>
        </Card>

        {/* Destructive data cleanup actions — kept near the bottom so it
            stays out of the way during normal subscription / flag work. */}
        <DataCleanupCard
          apiFetch={apiFetch}
          orgId={orgId}
          orgSlug={org.slug}
          orgName={org.name}
          onWiped={() => fetchOrg()}
        />

        {/* Owner + Members */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-slate-200/60">
            <CardHeader><CardTitle className="text-sm">Owner</CardTitle></CardHeader>
            <CardContent>
              {org.owner ? (
                <>
                  <p className="text-sm font-medium text-slate-900">{org.owner.name}</p>
                  <p className="text-xs text-slate-500">{org.owner.email}</p>
                </>
              ) : <p className="text-xs text-slate-400">No owner</p>}
            </CardContent>
          </Card>
          <Card className="border-slate-200/60">
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Members ({org.members?.length || 0})</CardTitle></CardHeader>
            <CardContent className="space-y-1.5 max-h-56 overflow-y-auto">
              {(org.members || []).map(m => (
                <div key={m.membership_id || m.user_id} className="flex items-center justify-between text-xs">
                  <span className="text-slate-700">{m.user?.name || m.user?.email || m.user_id}</span>
                  <Badge variant="outline" className="text-[10px]">{m.role}</Badge>
                </div>
              ))}
              {(!org.members || org.members.length === 0) && <p className="text-xs text-slate-400">No members</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </SALayout>
  );
}
